import React from 'react';
import { IBlockData } from '@core/typings';
import { BasicType } from '@core/constants';
import { createBlock } from '@core/utils/createBlock';
import { merge } from 'lodash';
import { BasicBlock } from '@core/components/BasicBlock';
import {
	formatDynamicTextValue,
	getAncestorPinnedListing,
	parseDynamicTextHelperExpression,
	resolveBoundListing,
	resolveMergeTagValue,
	t,
} from '@core/utils';
import {
	getPreviewResolutionDataSource,
	resolveAttachedListingMergeTags,
} from '../../../utils/resolveAttachedListingMergeTags';

const MERGE_TAG_PATTERN =
	/(\{\{\{\{raw\}\}\}\})?\{\{\s*([^{}]+?)\s*\}\}(\{\{\{\{\/raw\}\}\}\})?/gi;
const IF_MERGE_TAG_PATTERN =
	/\{\{#if\s+([\w.-]+)\}\}\s*\{\{([^{}]+?)\}\}\s*\{\{else\}\}([\s\S]*?)\{\{\/if\}\}/g;

function resolveDataSourceMergeTags(
	template: string | undefined,
	dataSource?: Record<string, any>,
) {
	if (!template || !dataSource) return template || '';

	const getFormattedResolvedValue = (
		resolvedValue: unknown,
		resolvedFormat?: string,
		resolvedMaxLength?: number | null,
	): string | null => {
		if (
			typeof resolvedValue === 'string' ||
			typeof resolvedValue === 'number' ||
			typeof resolvedValue === 'boolean'
		) {
			return formatDynamicTextValue(resolvedValue, resolvedFormat, resolvedMaxLength);
		}

		if (Array.isArray(resolvedValue)) {
			const firstValue = resolvedValue.find(
				item =>
					typeof item === 'string' ||
					typeof item === 'number' ||
					typeof item === 'boolean',
			);
			if (firstValue !== undefined) {
				return formatDynamicTextValue(firstValue, resolvedFormat, resolvedMaxLength);
			}
		}

		return null;
	};

	const withConditionalsResolved = template.replace(
		IF_MERGE_TAG_PATTERN,
		(_, conditionKey, trueExpression, fallbackText) => {
			const fallback = String(fallbackText || '').trim();
			const parsedHelper = parseDynamicTextHelperExpression(
				String(trueExpression || '').trim(),
			);
			const actualKey = String(parsedHelper?.key || conditionKey || '').trim();
			if (!actualKey) return fallback;

			const resolvedValue = resolveMergeTagValue(dataSource, actualKey);
			const formattedValue = getFormattedResolvedValue(
				resolvedValue,
				parsedHelper?.format,
				parsedHelper?.maxLength,
			);

			if (formattedValue !== null && formattedValue.trim() !== '') {
				return formattedValue;
			}

			return fallback;
		},
	);

	return withConditionalsResolved.replace(MERGE_TAG_PATTERN, match => {
		const keyMatch = match.match(/\{\{\s*([^{}]+?)\s*\}\}/);
		const rawKey = String(keyMatch?.[1] || '');
		const trimmedRawKey = rawKey.trim();

		// Skip orphaned Handlebars block tokens
		if (
			trimmedRawKey.startsWith('#') ||
			trimmedRawKey.startsWith('/') ||
			trimmedRawKey === 'else'
		)
			return '';

		const parsedHelper = parseDynamicTextHelperExpression(trimmedRawKey);
		const pipeIdx = trimmedRawKey.indexOf('|');
		const actualKey = parsedHelper
			? parsedHelper.key
			: pipeIdx >= 0
				? trimmedRawKey.slice(0, pipeIdx).trim()
				: trimmedRawKey;
		const fallbackText =
			!parsedHelper && pipeIdx >= 0 ? trimmedRawKey.slice(pipeIdx + 1).trim() : '';

		const resolvedValue = resolveMergeTagValue(dataSource, actualKey);
		const formattedValue = getFormattedResolvedValue(
			resolvedValue,
			parsedHelper?.format,
			parsedHelper?.maxLength,
		);

		// A genuinely blank resolved value (a real field that exists but is empty)
		// is not a resolved value — fall through to the tag's own fallback text,
		// or the raw tag, same as an unresolved one. Never render nothing.
		if (formattedValue !== null && formattedValue.trim() !== '') {
			return formattedValue;
		}

		if (fallbackText) return fallbackText;
		return match;
	});
}

export type ITable = IBlockData<{}, { content: string }>;

export const Table = createBlock<ITable>({
	get name() {
		return t('Table');
	},
	type: BasicType.TABLE,
	create: payload => {
		const defaultData: ITable = {
			type: BasicType.TABLE,
			data: {
				value: {
					content:
						'<tr><td style="padding: 10px; border: 1px solid #ddd;">Header 1</td><td style="padding: 10px; border: 1px solid #ddd;">Header 2</td><td style="padding: 10px; border: 1px solid #ddd;">Header 3</td></tr><tr><td style="padding: 10px; border: 1px solid #ddd;">Cell 1-1</td><td style="padding: 10px; border: 1px solid #ddd;">Cell 1-2</td><td style="padding: 10px; border: 1px solid #ddd;">Cell 1-3</td></tr><tr><td style="padding: 10px; border: 1px solid #ddd;">Cell 2-1</td><td style="padding: 10px; border: 1px solid #ddd;">Cell 2-2</td><td style="padding: 10px; border: 1px solid #ddd;">Cell 2-3</td></tr>',
				},
			},
			attributes: {
				'container-background-color': '#FFFFFF',
			},
			children: [],
		};
		return merge(defaultData, payload);
	},
	validParentType: [BasicType.COLUMN],
	render(params) {
		const { data, mode } = params;
		const isPreviewMode = Boolean(params.dataSource?.previewMode);
		// This block offers a listing picker, so it has to honour one — its own
		// selection first, else the listing pinned by an ancestor Listing Wrapper
		// or listing column. Without this the picker accepted a listing and the
		// table ignored it, and a table inside a listing scope never hydrated.
		// Listing tags resolve first, then the preview data source fills whatever
		// is left — same order as the Text block.
		// An enclosing Listing Wrapper / listing column WINS — see the Text block.
		const selectedListing =
			getAncestorPinnedListing(params.context, params.idx) ||
			resolveBoundListing(data.data.value);
		// TODO(raster): a listing tag here still resolves to bare text in the raster
		// copy and loses its underline — this block does not pass the merge-field
		// wrapper the Text block does. Add it when this block's raster output matters.
		const listingResolved = resolveAttachedListingMergeTags(data.data.value.content, {
			listing: selectedListing,
		});
		// Skipped in rasterMode: nothing there can resolve a user.* tag, so the call
		// could only swap the tag for its send-time fallback — losing the token the
		// raster copy needs to print a field name and carry the underline.
		const content =
			mode === 'testing' && isPreviewMode && !params.dataSource?.rasterMode
				? resolveDataSourceMergeTags(
						listingResolved,
						getPreviewResolutionDataSource(params.dataSource, selectedListing),
					)
				: listingResolved;

		return (
			<BasicBlock
				params={params}
				tag='mj-table'
			>
				{content}
			</BasicBlock>
		);
	},
});
